import { displayUnitSymbol } from '../constants/units'
import type { Unit } from '../enums'
import { COST_ENTRY } from '../locales/de/cost-entry'

import { formatNumber } from './format-number'
import { formatSpan } from './product-property'

export const formatEuro = (
  value: number,
  options?: Intl.NumberFormatOptions,
) => {
  return formatNumber(value, { style: 'currency', currency: 'EUR', ...options })
}

export const formatEuroWhole = (value: number) =>
  formatEuro(value, { maximumFractionDigits: 0 })

/**
 * A figure stated as what it adds to a total. The sign comes from the value:
 * an option's price formula may be a discount (`resolveOptionPrice` keeps a
 * negative amount as a plain amount), and a hard-coded "+" would render that
 * as "+-1.000 €".
 *
 * Zero keeps its "+" so a surface toggling between an empty and a filled
 * selection does not flip the sign back and forth.
 */
export const formatEuroWholeSigned = (value: number) =>
  value < 0 ? formatEuroWhole(value) : `+${formatEuroWhole(value)}`

/**
 * A price stated per the unit the thing is sold in.
 *
 * One builder, because four surfaces print it — a measure's linked products,
 * the products suggested for it, a property card, a match row — and written
 * out per surface they came out as `40 €/m²` here and `40 € / m²` two rows
 * down. Null where there is no price; a thing with no unit is just a price.
 */
export function formatPricePerUnit(
  price: number | null | undefined,
  unit: Unit | null | undefined,
): string | null {
  if (price == null) return null
  return perUnit(unit)(formatEuroWhole(price))
}

/** The `/m²` a price per a unit ends in, as the tail `formatSpan` finishes a
 *  line with. A thing with no unit is just a price. */
const perUnit = (unit: Unit | null | undefined) => {
  const symbol = displayUnitSymbol(unit ?? null)
  return (figure: string) => (symbol ? `${figure}/${symbol}` : figure)
}

/**
 * A price span, as the line a bound on it is read out as.
 *
 * The same shape `formatBoundsOnAxis` draws, through the same `formatSpan` —
 * the matrix sets the two side by side in one table row, and written out
 * separately they disagreed about the dash.
 *
 * To the cent, unlike the lists that print a product's own price: this is the
 * readout of the field that writes it, and rounded to the euro an operator
 * cannot tell the 12,50 they typed from the 13 the cell shows — on the one
 * figure the margin is set by. A whole euro still prints whole.
 *
 * The two words are the caller's, because `shared/utils` holds no German.
 */
export function formatPriceSpan(
  min: number | null,
  max: number | null,
  unit: Unit | null | undefined,
  words: { from: string; to: string },
): string | null {
  return formatSpan(
    min,
    max,
    {
      figure: value => formatNumber(value, CENTS),
      withUnit: text => perUnit(unit)(`${text} €`),
    },
    words,
  )
}

/** As many decimals as the figure carries, up to the two a price is stored
 *  with. */
const CENTS = { minimumFractionDigits: 0, maximumFractionDigits: 2 } as const

/**
 * A percentage as every surface prints one: up to two decimals, with the sign
 * the cost table sets.
 *
 * Here beside the money because the two are read together — a mark-up on a
 * stage, a discount on a position — and written per surface they disagreed
 * about how many decimals a percentage carries.
 */
export const formatPercent = (value: number): string =>
  COST_ENTRY.price.percent(formatNumber(value, CENTS))
